import { z } from 'zod';

import { passwordField, phoneField } from '../_shared/schema';

/**
 * One form, two acts, one resolver.
 *
 * The screen carries a single `<form>` with two submit buttons rather than two
 * forms or a method switcher: the accepted set is the `AUTH_METHODS` setting's
 * and the two methods separate on which button was pressed, not on which page
 * the operator is standing on.
 *
 * The pressed button is therefore carried as a **form value**, because the
 * resolver has to see it. The password's own bounds apply on one act and the
 * field is not filled at all on the other, and a schema that could not tell the
 * two apart would either demand a password from a code sign-in or accept a
 * four-character one.
 *
 * The types are derived with `z.input` / `z.output` and never written in
 * parallel: the input is what the controls hold, the output is what the request
 * carries after the digit fold, and a hand-written twin of either drifts from
 * the resolver on the first edit.
 */
export const loginSchema = z
  .object({
    phone: phoneField,
    password: z.string(),
    method: z.enum(['otp', 'password']),
  })
  .superRefine((values, context) => {
    if (values.method !== 'password') return;

    const result = passwordField.safeParse(values.password);
    if (result.success) return;

    for (const issue of result.error.issues) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: issue.message });
    }
  });

/** What the controls hold — raw, in whatever digit set the keyboard produced. */
export type LoginFields = z.input<typeof loginSchema>;

/** What the request carries — folded to ASCII and bounds-checked. */
export type LoginValues = z.output<typeof loginSchema>;
